import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { MapPin, Plus } from "lucide-react";
import { useManagedList, useStationManagementApi } from "../../hooks/useStationManagement";
import GroupFormModal from "./GroupFormModal";
import LocationFormModal from "./LocationFormModal";
import { DAYS, buildOperatingHours, defaultSchedule, parseOperatingHours, validateSchedule } from "./operatingHours";
import {
  Button,
  ConfirmDialog,
  EMAIL_RE,
  FormField,
  FormSection,
  Overlay,
  PHONE_RE,
  SearchableSelect,
  SelectField,
  TextField,
  Toggle,
  inputClass,
} from "./ui";

const EMPTY = {
  station_name: "",
  display_name: "",
  station_type: "DC",
  capacity_kw: "",
  number_of_guns: "2",
  status: "active",
  group_id: "",
  location_id: "",
  price_per_kwh: "",
  billing_enabled: true,
  protocol_version: "OCPP 1.6J",
  vendor: "",
  model: "",
  identity: "",
  phone: "",
  email: "",
  installation_date: "",
  last_maintenance_date: "",
};

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "upcoming", label: "Upcoming" },
];
const TYPE_OPTIONS = [
  { value: "DC", label: "DC" },
  { value: "AC", label: "AC" },
];
const PROTOCOL_SUGGESTIONS = ["OCPP 1.6J", "OCPP 2.0.1"];

function validate(form, hoursMode, schedule, locations) {
  const e = {};
  if (!form.station_name.trim()) e.station_name = "Station name is required";
  const cap = Number(form.capacity_kw);
  if (form.capacity_kw === "" || Number.isNaN(cap) || cap <= 0) e.capacity_kw = "Enter a capacity greater than 0";
  const guns = Number(form.number_of_guns);
  if (form.number_of_guns === "" || !Number.isInteger(guns) || guns <= 0) e.number_of_guns = "Enter a whole number greater than 0";
  if (!form.group_id) e.group_id = "Select a group";
  if (!form.location_id) e.location_id = "Select a location";
  else if (!locations.some((l) => l.location_id === form.location_id)) e.location_id = "This location is not part of the selected group";
  if (form.price_per_kwh !== "") {
    const p = Number(form.price_per_kwh);
    if (Number.isNaN(p) || p < 0) e.price_per_kwh = "Enter a price of 0 or more";
  }
  if (!form.identity.trim()) e.identity = "Station identity is required (it is also the station ID)";
  else if (!/^[A-Za-z0-9._:-]{1,50}$/.test(form.identity.trim())) e.identity = "Up to 50 letters, numbers and . _ : - (no spaces)";
  if (!form.phone.trim()) e.phone = "Phone is required";
  else if (!PHONE_RE.test(form.phone.trim())) e.phone = "Enter a valid phone number";
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email";
  if (form.installation_date && form.last_maintenance_date && form.last_maintenance_date < form.installation_date)
    e.last_maintenance_date = "Can't be before the installation date";
  if (hoursMode === "custom") {
    const msg = validateSchedule(schedule);
    if (msg) e.operating_hours = msg;
  }
  return e;
}

// Create (station = null) or edit a charging station. Groups and locations can be
// created from inside the form; the form's other values are kept while doing so.
export default function StationFormDrawer({ open, station, groups, groupsLoading, onGroupsChanged, onClose, onSaved }) {
  const api = useStationManagementApi();
  const isEdit = Boolean(station?.station_id);

  const [form, setForm] = useState(EMPTY);
  const [hoursMode, setHoursMode] = useState("24x7");
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [locationModal, setLocationModal] = useState(false);
  const formTopRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setDirty(false);
    if (station) {
      setForm(fromStation(station));
      const oh = parseOperatingHours(station.operating_hours);
      setHoursMode(oh.mode);
      setSchedule(oh.schedule);
    } else {
      setForm(EMPTY);
      setHoursMode("24x7");
      setSchedule(defaultSchedule());
    }
  }, [open, station]);

  // locations of the selected group only
  const fetchLocations = useCallback(() => api.listLocations(form.group_id), [api, form.group_id]);
  const {
    items: fetchedLocations,
    setItems: setLocations,
    isLoading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useManagedList(open && form.group_id ? fetchLocations : null, [open, fetchLocations]);

  // guard against a list still loading from a previously selected group
  const locations = useMemo(() => fetchedLocations.filter((l) => l.group_id === form.group_id), [fetchedLocations, form.group_id]);

  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({
        value: g.group_id,
        label: `Group ${g.group_id} · ${g.group_name}`,
        sub: [g.owner_name, g.status === "inactive" ? "inactive" : ""].filter(Boolean).join(" · ") || undefined,
      })),
    [groups]
  );
  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        value: l.location_id,
        label: `Location ${l.location_id} · ${l.location_name || l.address_line1}`,
        sub: [l.address_line1, l.city, l.state].filter(Boolean).join(", "),
      })),
    [locations]
  );

  const update = (name, value) => {
    setDirty(true);
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const selectGroup = (groupId) => {
    setDirty(true);
    setForm((f) => (f.group_id === groupId ? f : { ...f, group_id: groupId, location_id: "" }));
    setErrors((e) => ({ ...e, group_id: undefined, location_id: undefined }));
  };

  const setDay = (day, patch) => {
    setDirty(true);
    setSchedule((s) => ({ ...s, [day]: { ...s[day], ...patch } }));
    if (errors.operating_hours) setErrors((e) => ({ ...e, operating_hours: undefined }));
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (saving) return;
    const found = validate(form, hoursMode, schedule, locations);
    setErrors(found);
    if (Object.keys(found).length) {
      toast.error("Please fix the highlighted fields");
      formTopRef.current?.closest(".overflow-y-auto")?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        is_update: isEdit,
        // station_id is always the station identity
        station_id: isEdit ? station.station_id : form.identity.trim(),
        station_name: form.station_name.trim(),
        display_name: form.display_name.trim(),
        station_type: form.station_type,
        capacity_kw: Number(form.capacity_kw),
        number_of_guns: Number(form.number_of_guns),
        status: form.status,
        group_id: form.group_id,
        location_id: form.location_id,
        price_per_kwh: form.price_per_kwh === "" ? 0 : Number(form.price_per_kwh),
        billing_enabled: form.billing_enabled,
        operating_hours: buildOperatingHours(hoursMode, schedule),
        protocol_version: form.protocol_version.trim(),
        vendor: form.vendor.trim(),
        model: form.model.trim(),
        identity: form.identity.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        installation_date: form.installation_date,
        last_maintenance_date: form.last_maintenance_date,
      };
      const saved = await api.saveStation(payload);
      toast.success(isEdit ? `Station ${saved.station_id} updated` : `Station ${saved.station_id} · ${saved.station_name} created`);
      setDirty(false);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(err.message || "Could not save the station");
    } finally {
      setSaving(false);
    }
  };

  const selectedGroup = groups.find((g) => g.group_id === form.group_id);

  return (
    <>
      <Overlay
        open={open}
        onClose={requestClose}
        variant="drawer"
        width="max-w-3xl"
        title={isEdit ? `Edit station ${station.station_id}` : "Add charging station"}
        subtitle={isEdit ? station.station_name : "Fields marked * are required."}
        footer={
          <>
            <Button onClick={requestClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={submit}>{isEdit ? "Save changes" : "Create charging station"}</Button>
          </>
        }
      >
        <form ref={formTopRef} onSubmit={submit} className="space-y-4" noValidate>
          {/* A: basic */}
          <FormSection letter="A" title="Basic information">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Station ID" hint={isEdit ? "Station ID can't be changed." : "Always the same as the station identity (section E)."}>
                <div className="flex h-9 items-center truncate rounded-lg border border-neutral-200 bg-neutral-50 px-3 font-mono text-[12.5px] text-neutral-600">
                  {isEdit ? station.station_id : form.identity.trim() || <span className="font-sans text-neutral-400">Enter the station identity below</span>}
                </div>
              </FormField>
              <SelectField label="Status" name="status" form={form} errors={errors} onChange={update} options={STATUS_OPTIONS} />
              <TextField label="Station name" name="station_name" required form={form} errors={errors} onChange={update} placeholder="e.g. HGCL Hyderabad 01" maxLength={100} />
              <TextField label="Display name" name="display_name" form={form} errors={errors} onChange={update} placeholder="Name shown to customers" maxLength={100} />
              <SelectField label="Station type" name="station_type" form={form} errors={errors} onChange={update} options={TYPE_OPTIONS} />
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Capacity (kW)" name="capacity_kw" type="number" min="0" step="0.1" required form={form} errors={errors} onChange={update} placeholder="60" />
                <TextField label="Number of guns" name="number_of_guns" type="number" min="1" step="1" required form={form} errors={errors} onChange={update} />
              </div>
            </div>
          </FormSection>

          {/* B: group */}
          <FormSection letter="B" title="Group" description="The owner group this station belongs to.">
            <FormField label="Select group" required error={errors.group_id}>
              <SearchableSelect
                value={form.group_id}
                onChange={selectGroup}
                options={groupOptions}
                placeholder="Search groups by ID or name"
                loading={groupsLoading}
                error={errors.group_id}
                emptyContent="No groups yet"
              />
            </FormField>
            <Button variant="link" size="sm" icon={Plus} className="mt-2" onClick={() => setGroupModal(true)}>
              Create new group
            </Button>
          </FormSection>

          {/* C: location */}
          <FormSection letter="C" title="Location" description={selectedGroup ? `Only locations of Group ${selectedGroup.group_id} · ${selectedGroup.group_name} are listed.` : "Choose a group first."}>
            {!form.group_id ? (
              <div className="rounded-lg border border-dashed border-neutral-200 px-4 py-6 text-center text-13 text-neutral-500">Select a group to see its locations.</div>
            ) : locationsError ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-13 text-red-700">
                <span>Couldn't load locations: {locationsError}</span>
                <Button size="sm" onClick={refetchLocations}>Retry</Button>
              </div>
            ) : !locationsLoading && locations.length === 0 ? (
              <div className="flex flex-col items-center rounded-lg border border-dashed border-neutral-200 px-4 py-6 text-center">
                <MapPin className="h-5 w-5 text-neutral-400" />
                <p className="mt-2 text-13 font-medium text-neutral-800">This group has no locations yet</p>
                <p className="mt-0.5 text-xs text-neutral-500">Create one now; it will be selected automatically.</p>
                <Button variant="primary" size="sm" icon={Plus} className="mt-3" onClick={() => setLocationModal(true)}>
                  Create new location
                </Button>
                {errors.location_id && <p className="mt-2 text-xs text-red-600">{errors.location_id}</p>}
              </div>
            ) : (
              <>
                <FormField label="Select location" required error={errors.location_id}>
                  <SearchableSelect
                    value={form.location_id}
                    onChange={(v) => update("location_id", v)}
                    options={locationOptions}
                    placeholder="Search locations"
                    loading={locationsLoading}
                    error={errors.location_id}
                  />
                </FormField>
                <Button variant="link" size="sm" icon={Plus} className="mt-2" onClick={() => setLocationModal(true)}>
                  Create new location
                </Button>
              </>
            )}
          </FormSection>

          {/* D: charging */}
          <FormSection letter="D" title="Charging configuration">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField label="Price per kWh (₹)" name="price_per_kwh" type="number" min="0" step="0.01" form={form} errors={errors} onChange={update} placeholder="18.00" />
              <div className="flex items-end">
                <div className="w-full">
                  <Toggle label="Billing enabled" description="Charge customers for sessions on this station" checked={form.billing_enabled} onChange={(v) => update("billing_enabled", v)} />
                </div>
              </div>
            </div>

            <FormField label="Operating hours" className="mt-4" error={errors.operating_hours}>
              <div className="inline-flex w-fit rounded-lg border border-neutral-200 p-0.5">
                {[
                  ["24x7", "Open 24x7"],
                  ["custom", "Custom hours"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setDirty(true);
                      setHoursMode(value);
                      setErrors((er) => ({ ...er, operating_hours: undefined }));
                    }}
                    className={`h-8 rounded-md px-3 text-13 font-medium transition-colors ${hoursMode === value ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FormField>
            {hoursMode === "custom" && (
              <div className="mt-3 divide-y divide-neutral-100 rounded-lg border border-neutral-200">
                {DAYS.map((day) => {
                  const s = schedule[day];
                  return (
                    <div key={day} className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <span className="w-24 text-13 font-medium capitalize text-neutral-800">{day}</span>
                      <input type="time" aria-label={`${day} opens`} value={s.open} disabled={s.closed} onChange={(e) => setDay(day, { open: e.target.value })} className={`${inputClass()} w-28`} />
                      <span className="text-xs text-neutral-400">to</span>
                      <input type="time" aria-label={`${day} closes`} value={s.close} disabled={s.closed} onChange={(e) => setDay(day, { close: e.target.value })} className={`${inputClass()} w-28`} />
                      <label className="ml-auto flex items-center gap-2 text-xs text-neutral-600">
                        <input type="checkbox" checked={s.closed} onChange={(e) => setDay(day, { closed: e.target.checked })} className="h-4 w-4 rounded border-neutral-300 accent-neutral-900" />
                        Closed
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </FormSection>

          {/* E: technical */}
          <FormSection letter="E" title="Technical information" description="Station identity is the OCPP charge point identity the charger connects with. It is also used as the station ID.">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField label="Station identity" name="identity" required form={form} errors={errors} onChange={update} placeholder="e.g. 206" maxLength={50} autoComplete="off" disabled={isEdit} hint={isEdit ? "Can't be changed: it is the station ID." : "Becomes the station ID."} />
              <TextField label="Protocol version" name="protocol_version" form={form} errors={errors} onChange={update} list="sm-protocols" maxLength={20} />
              <datalist id="sm-protocols">
                {PROTOCOL_SUGGESTIONS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              <TextField label="Vendor" name="vendor" form={form} errors={errors} onChange={update} maxLength={100} />
              <TextField label="Model" name="model" form={form} errors={errors} onChange={update} maxLength={100} />
              <TextField label="Phone" name="phone" type="tel" required form={form} errors={errors} onChange={update} placeholder="+91 98765 43210" maxLength={20} />
              <TextField label="Email" name="email" type="email" form={form} errors={errors} onChange={update} maxLength={255} />
            </div>
          </FormSection>

          {/* F: installation */}
          <FormSection letter="F" title="Installation">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField label="Installation date" name="installation_date" type="date" form={form} errors={errors} onChange={update} hint={isEdit ? undefined : "Defaults to today if left empty."} />
              <TextField label="Last maintenance date" name="last_maintenance_date" type="date" form={form} errors={errors} onChange={update} />
            </div>
          </FormSection>
          <button type="submit" className="hidden" />
        </form>
      </Overlay>

      <GroupFormModal
        open={groupModal}
        group={null}
        layer="z-[60]"
        onClose={() => setGroupModal(false)}
        onSaved={(g) => {
          onGroupsChanged?.(g);
          selectGroup(g.group_id);
        }}
      />

      <LocationFormModal
        open={locationModal}
        location={null}
        groups={groups}
        defaultGroupId={form.group_id}
        lockGroup
        layer="z-[60]"
        onClose={() => setLocationModal(false)}
        onSaved={(l) => {
          // add it right away so it can be selected, then refresh from the server
          setLocations((list) => (list.some((x) => x.location_id === l.location_id) ? list : [...list, l]));
          update("location_id", l.location_id);
          refetchLocations();
        }}
      />

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard changes?"
        message="You have unsaved changes to this station. They will be lost if you close the form."
        confirmLabel="Discard"
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          setDirty(false);
          onClose();
        }}
      />
    </>
  );
}

function fromStation(s) {
  const str = (v) => (v === null || v === undefined ? "" : String(v));
  return {
    station_name: str(s.station_name),
    display_name: str(s.display_name),
    station_type: (s.station_type || "DC").toUpperCase() === "AC" ? "AC" : "DC",
    capacity_kw: str(s.capacity_kw),
    number_of_guns: str(s.number_of_guns),
    status: s.status || "active",
    group_id: str(s.group_id),
    location_id: str(s.location_id),
    price_per_kwh: str(s.price_per_kwh),
    billing_enabled: s.billing_enabled !== false,
    protocol_version: str(s.protocol_version),
    vendor: str(s.vendor),
    model: str(s.model),
    identity: str(s.identity),
    phone: str(s.phone),
    email: str(s.email),
    installation_date: str(s.installation_date),
    last_maintenance_date: str(s.last_maintenance_date),
  };
}
